import * as React from 'react'

import { XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { Content, Header, View } from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IAssignmentsStore } from '~/client/src/shared/stores/BaseAssignments.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import {
  DELETE_ASSIGNMENTS,
  SAVE_ASSIGNMENTS,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import InitialState from '~/client/src/shared/stores/InitialState'

import TagsSelector from '../TagsSelector/TagsSelector'
import ResponsibilitiesPanelStore from './ResponsibilitiesPanel.store'

import Colors from '~/client/src/shared/theme.module.scss'

// localization: translated

const availableTypes = [
  TagType.User,
  TagType.Team,
  TagType.Role,
  TagType.Company,
]

interface IProps {
  selectedEntitiesIds: string[]
  onClose: () => void
  domainStore: IAssignmentsStore

  eventsStore?: DesktopEventStore
  tagsStore?: TagsStore
  state?: InitialState
}

@inject('eventsStore', 'tagsStore', 'state')
@observer
export default class ResponsibilitiesPanel extends React.Component<IProps> {
  private readonly clearPostEventCallback: () => void
  private readonly store: ResponsibilitiesPanelStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new ResponsibilitiesPanelStore(
      props.tagsStore,
      props.domainStore,
      props.state,
      props.onClose,
      props.selectedEntitiesIds,
    )

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      this.store.handlePostEventCallback,
    )
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { onClose, selectedEntitiesIds } = this.props

    if (!selectedEntitiesIds.length) {
      onClose()
    }

    if (selectedEntitiesIds !== prevProps.selectedEntitiesIds) {
      this.store.init(selectedEntitiesIds)
    }
  }

  public componentWillUnmount() {
    this.clearPostEventCallback()
  }

  public render() {
    const { loading } = this.props.eventsStore.appState
    if (loading.get(SAVE_ASSIGNMENTS) || loading.get(DELETE_ASSIGNMENTS)) {
      return <Loader />
    }

    const { onClose } = this.props
    const {
      inputValue,
      clearInput,
      handleApply,
      defaultSelection,
      changeSelection,
      canApply,
    } = this.store

    return (
      <View className="px12">
        <Header className="pb3">
          <div className="row py12">
            <XIcon
              size={16}
              className="no-grow pointer mr12"
              color={Colors.neutral60}
              onClick={onClose}
            />
            <span className="text bold small-header">
              {Localization.translator.assignUsersOrCategories}
            </span>
            <Button
              className="bold"
              variant="primary-outline"
              disabled={!canApply}
              onClick={handleApply}
            >
              {Localization.translator.apply}
            </Button>
          </div>
          <StruxhubInput
            label={Localization.translator.searchUserCompanyTeamOrRole}
            value={inputValue}
            isRequiredTextHidden={true}
            onChange={this.handleInputChange}
            onValueReset={clearInput}
          />
        </Header>
        <Content>
          <TagsSelector
            onChange={changeSelection}
            availableTypes={availableTypes}
            initSelection={defaultSelection}
            searchKey={inputValue}
            separatedCategories={[TagType.Company]}
          />
        </Content>
      </View>
    )
  }

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.changeInput(event.target.value)
  }
}
